import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import {
  Plus, Edit2, Save, MoreVertical, UserCheck, Handshake,
  Target, DollarSign, Percent
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const Vendors = ({ contacts, onRefresh }) => {
  const [vendors, setVendors] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [activeTab, setActiveTab] = useState("vendors");
  const [vendorDialogOpen, setVendorDialogOpen] = useState(false);
  const [referralDialogOpen, setReferralDialogOpen] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [vendorForm, setVendorForm] = useState({
    name: "", email: "", phone: "", company: "", services_offered: "",
    referral_fee_type: "percentage", referral_fee_value: "", payment_terms: "", notes: ""
  });
  const [referralForm, setReferralForm] = useState({
    vendor_id: "none", contact_id: "none", lead_source: "vendor_referral", notes: ""
  });

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(value || 0);
  };

  const fetchVendorData = useCallback(async () => {
    try {
      const [vendorsRes, referralsRes] = await Promise.all([
        axios.get(`${API}/vendors`),
        axios.get(`${API}/referrals`)
      ]);
      setVendors(vendorsRes.data);
      setReferrals(referralsRes.data);
    } catch (error) {
      console.error("Error fetching vendor data:", error);
    }
  }, []);

  useEffect(() => {
    fetchVendorData();
  }, [fetchVendorData]);

  const handleOpenVendorDialog = (vendor = null) => {
    if (vendor) {
      setEditingVendor(vendor);
      setVendorForm({
        name: vendor.name || "",
        email: vendor.email || "",
        phone: vendor.phone || "",
        company: vendor.company || "",
        services_offered: (vendor.services_offered || []).join(", "),
        referral_fee_type: vendor.referral_fee_type || "percentage",
        referral_fee_value: vendor.referral_fee_value?.toString() || "",
        payment_terms: vendor.payment_terms || "",
        notes: vendor.notes || ""
      });
    } else {
      setEditingVendor(null);
      setVendorForm({ name: "", email: "", phone: "", company: "", services_offered: "", referral_fee_type: "percentage", referral_fee_value: "", payment_terms: "", notes: "" });
    }
    setVendorDialogOpen(true);
  };

  const handleSaveVendor = async () => {
    try {
      const payload = {
        ...vendorForm,
        services_offered: vendorForm.services_offered.split(",").map(s => s.trim()).filter(s => s),
        referral_fee_value: parseFloat(vendorForm.referral_fee_value) || 0
      };
      if (editingVendor) {
        await axios.put(`${API}/vendors/${editingVendor.id}`, payload);
      } else {
        await axios.post(`${API}/vendors`, payload);
      }
      setVendorDialogOpen(false);
      fetchVendorData();
    } catch (error) {
      console.error("Error saving vendor:", error);
    }
  };

  const handleCreateReferral = async () => {
    try {
      if (referralForm.vendor_id === "none" || referralForm.contact_id === "none") {
        alert("Please select both vendor and contact");
        return;
      }
      await axios.post(`${API}/referrals`, {
        vendor_id: referralForm.vendor_id,
        contact_id: referralForm.contact_id,
        lead_source: referralForm.lead_source,
        notes: referralForm.notes
      });
      setReferralDialogOpen(false);
      setReferralForm({ vendor_id: "none", contact_id: "none", lead_source: "vendor_referral", notes: "" });
      fetchVendorData();
      onRefresh();
    } catch (error) {
      console.error("Error creating referral:", error);
    }
  };

  const handleUpdateReferralStatus = async (referralId, newStatus, dealValue = null) => {
    try {
      const payload = { status: newStatus };
      if (dealValue !== null) {
        payload.deal_value = dealValue;
      }
      await axios.put(`${API}/referrals/${referralId}`, payload);
      fetchVendorData();
    } catch (error) {
      console.error("Error updating referral:", error);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      introduced: "bg-purple-100 text-purple-600",
      active: "bg-blue-100 text-blue-600",
      service_completed: "bg-green-100 text-green-600",
      fee_invoiced: "bg-yellow-100 text-yellow-600",
      fee_paid: "bg-emerald-100 text-emerald-600",
      lost: "bg-slate-100 text-slate-600"
    };
    return styles[status] || "bg-slate-100 text-slate-600";
  };

  const getVendorName = (vendorId) => {
    const vendor = vendors.find(v => v.id === vendorId);
    return vendor ? vendor.name : 'Unknown';
  };

  const getContactName = (contactId) => {
    const contact = contacts.find(c => c.id === contactId);
    return contact ? contact.name : 'Unknown';
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="vendors-view">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-cabinet text-3xl font-bold text-slate-900">Vendor Directory</h2>
          <p className="text-slate-500 font-manrope">Manage vendors, referral partners & commissions</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setReferralDialogOpen(true)} variant="outline">
            <UserCheck size={18} className="mr-2" /> Log Referral
          </Button>
          <Button onClick={() => handleOpenVendorDialog()} className="bg-royal-blue-700 hover:bg-royal-blue-800">
            <Plus size={18} className="mr-2" /> Add Vendor
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Vendors</p>
                <p className="font-cabinet text-2xl font-bold">{vendors.length}</p>
              </div>
              <Handshake className="text-royal-blue-600" size={24} />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Referrals</p>
                <p className="font-cabinet text-2xl font-bold">{referrals.length}</p>
              </div>
              <UserCheck className="text-purple-600" size={24} />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Conversions</p>
                <p className="font-cabinet text-2xl font-bold">{referrals.filter(r => ['service_completed', 'fee_invoiced', 'fee_paid'].includes(r.status)).length}</p>
              </div>
              <Target className="text-green-600" size={24} />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Fees Owed</p>
                <p className="font-cabinet text-2xl font-bold text-orange-600">
                  {formatCurrency(referrals.filter(r => ['service_completed', 'fee_invoiced'].includes(r.status)).reduce((sum, r) => sum + (r.referral_fee || 0), 0))}
                </p>
              </div>
              <DollarSign className="text-orange-600" size={24} />
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="vendors">Vendors</TabsTrigger>
          <TabsTrigger value="referrals">Referral Pipeline</TabsTrigger>
        </TabsList>

        <TabsContent value="vendors" className="mt-4">
          <Card>
            <CardContent className="p-0">
              <table className="w-full">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="text-left p-4 font-medium">Vendor</th>
                    <th className="text-left p-4 font-medium">Contact</th>
                    <th className="text-left p-4 font-medium">Services</th>
                    <th className="text-left p-4 font-medium">Referral Fee</th>
                    <th className="text-left p-4 font-medium">Stats</th>
                    <th className="text-right p-4 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {vendors.map(vendor => (
                    <tr key={vendor.id} className="border-b hover:bg-slate-50">
                      <td className="p-4">
                        <div>
                          <p className="font-medium">{vendor.name}</p>
                          <p className="text-sm text-slate-500">{vendor.company}</p>
                        </div>
                      </td>
                      <td className="p-4">
                        <p className="text-sm">{vendor.email}</p>
                        <p className="text-sm text-slate-500">{vendor.phone}</p>
                      </td>
                      <td className="p-4">
                        <div className="flex flex-wrap gap-1">
                          {(vendor.services_offered || []).slice(0, 2).map((s, i) => (
                            <Badge key={i} variant="outline" className="text-xs">{s}</Badge>
                          ))}
                          {(vendor.services_offered || []).length > 2 && (
                            <Badge variant="outline" className="text-xs">+{vendor.services_offered.length - 2}</Badge>
                          )}
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-1">
                          {vendor.referral_fee_type === 'percentage' ? (
                            <><Percent size={14} className="text-slate-400" /> <span className="font-medium">{vendor.referral_fee_value}%</span></>
                          ) : (
                            <span className="font-medium">{formatCurrency(vendor.referral_fee_value)} flat</span>
                          )}
                        </div>
                        <p className="text-xs text-slate-500">{vendor.payment_terms}</p>
                      </td>
                      <td className="p-4">
                        <p className="text-sm">Referrals: <span className="font-medium">{vendor.total_referrals || 0}</span></p>
                        <p className="text-sm">Conversions: <span className="font-medium text-green-600">{vendor.total_conversions || 0}</span></p>
                      </td>
                      <td className="p-4 text-right">
                        <Button variant="ghost" size="sm" onClick={() => handleOpenVendorDialog(vendor)}>
                          <Edit2 size={16} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {vendors.length === 0 && <p className="text-center py-8 text-slate-500">No vendors yet. Add your first referral partner!</p>}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="referrals" className="mt-4">
          <Card>
            <CardContent className="p-0">
              <table className="w-full">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="text-left p-4 font-medium">Referred Contact</th>
                    <th className="text-left p-4 font-medium">Referred By</th>
                    <th className="text-left p-4 font-medium">Lead Source</th>
                    <th className="text-left p-4 font-medium">Status</th>
                    <th className="text-left p-4 font-medium">Deal Value</th>
                    <th className="text-left p-4 font-medium">Referral Fee</th>
                    <th className="text-right p-4 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {referrals.map(referral => (
                    <tr key={referral.id} className="border-b hover:bg-slate-50">
                      <td className="p-4 font-medium">{getContactName(referral.contact_id)}</td>
                      <td className="p-4">{getVendorName(referral.vendor_id)}</td>
                      <td className="p-4"><Badge variant="outline">{referral.lead_source}</Badge></td>
                      <td className="p-4"><Badge className={getStatusBadge(referral.status)}>{referral.status.replace('_', ' ')}</Badge></td>
                      <td className="p-4">{referral.deal_value ? formatCurrency(referral.deal_value) : '-'}</td>
                      <td className="p-4 font-semibold text-green-600">{referral.referral_fee ? formatCurrency(referral.referral_fee) : '-'}</td>
                      <td className="p-4 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm"><MoreVertical size={16} /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleUpdateReferralStatus(referral.id, 'active')}>Mark Active</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => {
                              const dealValue = window.prompt("Enter deal value:");
                              if (dealValue) handleUpdateReferralStatus(referral.id, 'service_completed', parseFloat(dealValue));
                            }}>Mark Completed (Enter Deal Value)</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleUpdateReferralStatus(referral.id, 'fee_invoiced')}>Mark Fee Invoiced</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleUpdateReferralStatus(referral.id, 'fee_paid')}>Mark Fee Paid</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleUpdateReferralStatus(referral.id, 'lost')} className="text-red-600">Mark Lost</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {referrals.length === 0 && <p className="text-center py-8 text-slate-500">No referrals tracked yet</p>}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={vendorDialogOpen} onOpenChange={setVendorDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingVendor ? 'Edit Vendor' : 'Add Vendor'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input value={vendorForm.name} onChange={e => setVendorForm({...vendorForm, name: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input type="email" value={vendorForm.email} onChange={e => setVendorForm({...vendorForm, email: e.target.value})} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input value={vendorForm.phone} onChange={e => setVendorForm({...vendorForm, phone: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Company</Label>
                <Input value={vendorForm.company} onChange={e => setVendorForm({...vendorForm, company: e.target.value})} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Services Offered (comma separated)</Label>
              <Input placeholder="Consulting, Marketing, Legal..." value={vendorForm.services_offered} onChange={e => setVendorForm({...vendorForm, services_offered: e.target.value})} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Referral Fee Type</Label>
                <Select value={vendorForm.referral_fee_type} onValueChange={v => setVendorForm({...vendorForm, referral_fee_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Percentage (%)</SelectItem>
                    <SelectItem value="flat">Flat Fee ($)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Fee Value</Label>
                <Input type="number" placeholder={vendorForm.referral_fee_type === 'percentage' ? 'e.g., 10' : 'e.g., 500'} value={vendorForm.referral_fee_value} onChange={e => setVendorForm({...vendorForm, referral_fee_value: e.target.value})} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Payment Terms</Label>
              <Input placeholder="Net 30, Upon conversion, etc." value={vendorForm.payment_terms} onChange={e => setVendorForm({...vendorForm, payment_terms: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea value={vendorForm.notes} onChange={e => setVendorForm({...vendorForm, notes: e.target.value})} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVendorDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveVendor} className="bg-royal-blue-700 hover:bg-royal-blue-800">
              <Save size={16} className="mr-2" /> Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={referralDialogOpen} onOpenChange={setReferralDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Log New Referral</DialogTitle>
            <DialogDescription>Record a referral from a vendor partner</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Referring Vendor *</Label>
              <Select value={referralForm.vendor_id} onValueChange={v => setReferralForm({...referralForm, vendor_id: v})}>
                <SelectTrigger><SelectValue placeholder="Select vendor" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Select vendor...</SelectItem>
                  {vendors.map(v => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Referred Contact *</Label>
              <Select value={referralForm.contact_id} onValueChange={v => setReferralForm({...referralForm, contact_id: v})}>
                <SelectTrigger><SelectValue placeholder="Select contact" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Select contact...</SelectItem>
                  {contacts.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Lead Source</Label>
              <Select value={referralForm.lead_source} onValueChange={v => setReferralForm({...referralForm, lead_source: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="vendor_referral">Vendor Referral</SelectItem>
                  <SelectItem value="website">Website</SelectItem>
                  <SelectItem value="direct">Direct</SelectItem>
                  <SelectItem value="event">Event</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea placeholder="Additional details..." value={referralForm.notes} onChange={e => setReferralForm({...referralForm, notes: e.target.value})} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReferralDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateReferral} className="bg-royal-blue-700 hover:bg-royal-blue-800">
              <Save size={16} className="mr-2" /> Log Referral
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
